import React, { useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { AdminCaseDetail, DOCUMENT_LABELS, STATUS_LABELS } from "../types";
import { Alert, Spinner, StatusBadge, formatMoney } from "../common/ui";

const TABS = ["Overview", "Applicant", "Documents", "Financial", "Guarantor 1", "Guarantor 2", "Review", "Bank", "Activity Log"] as const;
const TAB_LABELS: Record<string, string> = {
  Overview: "نمای کلی", Applicant: "متقاضی", Documents: "مدارک", Financial: "مالی",
  "Guarantor 1": "ضامن اول", "Guarantor 2": "ضامن دوم", Review: "بررسی", Bank: "بانک", "Activity Log": "تاریخچه",
};

const NEXT_STATUS: Record<string, string[]> = {
  SUBMITTED: ["DOCUMENT_REVIEW"],
  DOCUMENT_REVIEW: ["CORRECTION_REQUIRED", "CREDIT_REVIEW"],
  CORRECTION_REQUIRED: ["DOCUMENT_REVIEW"],
  CREDIT_REVIEW: ["READY_FOR_BANK", "REJECTED"],
  READY_FOR_BANK: ["SENT_TO_BANK"],
  SENT_TO_BANK: ["BANK_REVIEW"],
  BANK_REVIEW: ["APPROVED", "REJECTED"],
  APPROVED: ["CLOSED"],
};

export function CaseDetail({ caseId, onBack }: { caseId: string; onBack: () => void }) {
  const [detail, setDetail] = useState<AdminCaseDetail | null>(null);
  const [tab, setTab] = useState<typeof TABS[number]>("Overview");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const d = await api.adminCaseDetail(caseId);
      setDetail(d);
    } catch (e) {
      setError((e as Error).message);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  async function withBusy(fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "خطایی رخ داد");
    } finally {
      setBusy(false);
    }
  }

  if (error && !detail) return <Alert kind="error">{error}</Alert>;
  if (!detail) return <Spinner />;

  const { case: c, profile } = detail;

  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={onBack}>← بازگشت به لیست پرونده‌ها</button>
      <div className="flex-between mt-2">
        <div>
          <h2 className="mb-0">{profile.storeName || "—"} <span className="text-sm">({c.caseNumber})</span></h2>
          <div className="text-sm">{profile.ownerFirstName} {profile.ownerLastName} — {formatMoney(c.requestedAmount)} ریال</div>
        </div>
        <StatusBadge status={c.status} labels={STATUS_LABELS} />
      </div>

      {error && <Alert kind="error">{error}</Alert>}

      <div className="tabs mt-3">
        {TABS.map((t) => (
          <button key={t} className={tab === t ? "active" : ""} onClick={() => setTab(t)}>{TAB_LABELS[t]}</button>
        ))}
      </div>

      {tab === "Overview" && <OverviewTab detail={detail} />}
      {tab === "Applicant" && <ApplicantTab detail={detail} />}
      {tab === "Documents" && <DocumentsTab detail={detail} busy={busy} withBusy={withBusy} filterEntity="APPLICANT" />}
      {tab === "Financial" && <FinancialTab detail={detail} busy={busy} withBusy={withBusy} />}
      {tab === "Guarantor 1" && <GuarantorTab detail={detail} seq={1} busy={busy} withBusy={withBusy} />}
      {tab === "Guarantor 2" && <GuarantorTab detail={detail} seq={2} busy={busy} withBusy={withBusy} />}
      {tab === "Review" && <ReviewTab detail={detail} busy={busy} withBusy={withBusy} />}
      {tab === "Bank" && <BankTab detail={detail} busy={busy} withBusy={withBusy} onExported={load} />}
      {tab === "Activity Log" && <ActivityTab detail={detail} />}
    </div>
  );
}

function OverviewTab({ detail }: { detail: AdminCaseDetail }) {
  const { case: c, profile, applicantAddress, completeness } = detail;
  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-title">اطلاعات کلی</div>
        <KV label="نام فروشگاه" value={profile.storeName} />
        <KV label="نوع فعالیت" value={profile.businessCategory} />
        <KV label="آدرس" value={applicantAddress?.line1} />
        <KV label="وضعیت محل" value={applicantAddress?.propertyStatus === "OWNED" ? "ملکی" : "اجاره‌ای"} />
        <KV label="بانک / شعبه" value={`${c.bankName} / ${c.bankBranch || "—"}`} />
      </div>
      <div className="card">
        <div className="card-title">تکمیل پرونده — {completeness.percent}٪</div>
        {completeness.sections.map((s) => (
          <div className="checklist-item" key={s.key}>
            <span className={`checklist-icon ${s.complete ? "ok" : "warn"}`}>{s.complete ? "✓" : "!"}</span>
            <span>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApplicantTab({ detail }: { detail: AdminCaseDetail }) {
  const { profile, applicantAddress } = detail;
  return (
    <div className="card">
      <div className="card-title">اطلاعات متقاضی</div>
      <KV label="نام صاحب جواز" value={`${profile.ownerFirstName} ${profile.ownerLastName}`} />
      <KV label="کد ملی" value={profile.ownerNationalId} />
      <KV label="موبایل" value={profile.ownerMobile} />
      <KV label="نوع فعالیت" value={profile.businessCategory} />
      <KV label="کد پستی" value={profile.postalCode} />
      <KV label="آدرس" value={applicantAddress?.line1} />
      <KV label="توضیحات" value={profile.notes} />
      <div className="card-subtitle mt-3">منبع داده هر فیلد</div>
      {Object.entries(profile.fieldSources || {}).map(([k, v]) => (
        <div key={k} className="text-sm">{k}: {v === "ZOODEX" ? "زودکس" : v === "MERCHANT" ? "پذیرنده" : v}</div>
      ))}
    </div>
  );
}

function KV({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex-between" style={{ padding: "6px 0", borderBottom: "1px solid var(--border)" }}>
      <span className="text-sm">{label}</span>
      <span style={{ fontWeight: 600 }}>{value || "—"}</span>
    </div>
  );
}

function DocumentsTab({
  detail, busy, withBusy, filterEntity, filterEntityId,
}: {
  detail: AdminCaseDetail; busy: boolean; withBusy: (fn: () => Promise<void>) => Promise<void>;
  filterEntity?: string; filterEntityId?: string;
}) {
  const rows = (detail.documents || []).filter((d) =>
    (!filterEntity || d.document.entityType === filterEntity) &&
    (!filterEntityId || d.document.entityId === filterEntityId) &&
    d.document.status !== "NOT_UPLOADED"
  );
  const [reasonFor, setReasonFor] = useState<Record<string, string>>({});

  if (rows.length === 0) return <p>مدرکی بارگذاری نشده است.</p>;

  return (
    <div>
      {rows.map(({ document: d, versions }) => {
        const current = versions[0];
        return (
          <div className="doc-row" key={d.id} style={{ flexDirection: "column", alignItems: "stretch" }}>
            <div className="flex-between">
              <div>
                <div style={{ fontWeight: 700 }}>{DOCUMENT_LABELS[d.documentType] || d.documentType}</div>
                {current && (
                  <div className="meta">
                    نسخه {current.versionNo} — {current.originalFilename} — {(current.sizeBytes / 1024).toFixed(0)} کیلوبایت
                    {" — "}<a href={api.downloadUrl(current.id)} target="_blank" rel="noreferrer">مشاهده/دانلود</a>
                  </div>
                )}
              </div>
              <StatusBadge status={d.status} labels={{
                NOT_UPLOADED: "بارگذاری نشده", UPLOADED: "در انتظار بررسی", PROCESSING: "پردازش",
                NEEDS_CORRECTION: "نیاز به اصلاح", VERIFIED: "تأیید شده", REJECTED: "رد شده", SUPERSEDED: "جایگزین شده",
              }} />
            </div>
            {d.status === "UPLOADED" && (
              <div className="flex gap-2 mt-2" style={{ flexWrap: "wrap" }}>
                <button className="btn btn-success btn-sm" disabled={busy} onClick={() => withBusy(() => api.adminReviewDocument(d.id, "approve").then(() => {}))}>تأیید</button>
                <input
                  placeholder="دلیل اصلاح/رد"
                  value={reasonFor[d.id] || ""}
                  onChange={(e: any) => setReasonFor({ ...reasonFor, [d.id]: e.target.value })}
                  style={{ width: 220 }}
                />
                <button className="btn btn-secondary btn-sm" disabled={busy} onClick={() => withBusy(() => api.adminReviewDocument(d.id, "request-correction", reasonFor[d.id]).then(() => {}))}>نیاز به اصلاح</button>
                <button className="btn btn-danger btn-sm" disabled={busy} onClick={() => withBusy(() => api.adminReviewDocument(d.id, "reject", reasonFor[d.id]).then(() => {}))}>رد مدرک</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FinancialTab({ detail, busy, withBusy }: { detail: AdminCaseDetail; busy: boolean; withBusy: (fn: () => Promise<void>) => Promise<void> }) {
  return (
    <div>
      <div className="card">
        <KV label="مبلغ تسهیلات درخواستی" value={`${formatMoney(detail.case.requestedAmount)} ریال`} />
      </div>
      <div className="card-subtitle">پیش‌بینی فروش / اسناد مالی</div>
      <DocumentsTab detail={detail} busy={busy} withBusy={withBusy} filterEntity="APPLICANT" />
    </div>
  );
}

function GuarantorTab({ detail, seq, busy, withBusy }: { detail: AdminCaseDetail; seq: 1 | 2; busy: boolean; withBusy: (fn: () => Promise<void>) => Promise<void> }) {
  const g = (detail.guarantors || []).find((x) => x.seq === seq);
  if (!g) return <p>اطلاعات این ضامن هنوز وارد نشده است.</p>;
  const addr = detail.guarantorAddresses?.[seq];
  return (
    <div>
      <div className="card">
        <KV label="نام" value={`${g.firstName} ${g.lastName}`} />
        <KV label="کد ملی" value={g.nationalId} />
        <KV label="موبایل" value={g.mobile} />
        <KV label="واحد صنفی" value={g.businessName} />
        <KV label="نوع فعالیت" value={g.businessActivity} />
        <KV label="آدرس" value={addr?.line1 || g.businessAddress} />
      </div>
      <div className="card-subtitle">مدارک</div>
      <DocumentsTab detail={detail} busy={busy} withBusy={withBusy} filterEntity="GUARANTOR" filterEntityId={g.id} />
    </div>
  );
}

function ReviewTab({ detail, busy, withBusy }: { detail: AdminCaseDetail; busy: boolean; withBusy: (fn: () => Promise<void>) => Promise<void> }) {
  const options = NEXT_STATUS[detail.case.status] || [];
  const [note, setNote] = useState("");
  return (
    <div className="card">
      <div className="card-title">تغییر وضعیت پرونده</div>
      <p>وضعیت فعلی: <StatusBadge status={detail.case.status} labels={STATUS_LABELS} /></p>
      {options.length === 0 && <p className="text-sm">در این وضعیت اقدام دیگری از این تب ممکن نیست.</p>}
      <div className="field">
        <label>یادداشت (اختیاری)</label>
        <input value={note} onChange={(e: any) => setNote(e.target.value)} />
      </div>
      <div className="flex gap-2" style={{ flexWrap: "wrap" }}>
        {options.map((s) => (
          <button key={s} className="btn btn-primary btn-sm" style={{ width: "auto" }} disabled={busy}
            onClick={() => withBusy(() => api.adminChangeStatus(detail.case.id, s, note).then(() => {}))}>
            انتقال به «{STATUS_LABELS[s]}»
          </button>
        ))}
      </div>
    </div>
  );
}

function BankTab({ detail, busy, withBusy, onExported }: { detail: AdminCaseDetail; busy: boolean; withBusy: (fn: () => Promise<void>) => Promise<void>; onExported: () => void }) {
  const [branch, setBranch] = useState(detail.case.bankBranch || "");
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [downloadId, setDownloadId] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId || jobStatus === "DONE" || jobStatus === "FAILED") return;
    const t = setInterval(async () => {
      const s = await api.adminBankExportJobStatus(jobId);
      setJobStatus(s.status);
      if (s.status === "DONE") {
        setDownloadId(s.bankExportId || null);
        onExported();
      }
    }, 1500);
    return () => clearInterval(t);
  }, [jobId, jobStatus]);

  return (
    <div className="card">
      <div className="card-title">اطلاعات بانک</div>
      <div className="field">
        <label>نام بانک</label>
        <input value={detail.case.bankName} disabled />
      </div>
      <div className="field">
        <label>شعبه</label>
        <div className="flex gap-2">
          <input value={branch} onChange={(e: any) => setBranch(e.target.value)} />
          <button className="btn btn-secondary btn-sm" style={{ width: "auto" }} disabled={busy} onClick={() => withBusy(() => api.adminSetBank(detail.case.id, branch).then(() => {}))}>ذخیره</button>
        </div>
      </div>

      <div className="card-subtitle mt-3">تولید بسته بانکی (Bank Package)</div>
      <p className="text-sm">بسته شامل ZIP استاندارد پوشه‌بندی‌شده + manifest.json برای بانک صادرات است.</p>
      <button
        className="btn btn-primary"
        style={{ width: "auto" }}
        disabled={busy || detail.case.status !== "READY_FOR_BANK" || !!jobId}
        onClick={async () => {
          const res = await api.adminBankExport(detail.case.id);
          setJobId(res.jobId);
          setJobStatus(res.status);
        }}
      >
        تولید بسته بانکی
      </button>
      {detail.case.status !== "READY_FOR_BANK" && !jobId && (
        <p className="text-sm mt-2">پرونده باید ابتدا به وضعیت «آماده ارسال به بانک» منتقل شود (تب بررسی).</p>
      )}
      {jobStatus && <p className="text-sm mt-2">وضعیت پردازش: {jobStatus}</p>}
      {downloadId && (
        <a className="btn btn-success mt-2" style={{ width: "auto", display: "inline-flex" }} href={api.adminBankExportDownloadUrl(downloadId)}>
          دانلود بسته بانکی (ZIP)
        </a>
      )}
    </div>
  );
}

function ActivityTab({ detail }: { detail: AdminCaseDetail }) {
  return (
    <div className="card">
      <div className="card-title">تاریخچه وضعیت</div>
      <ul className="timeline">
        {(detail.history || []).map((h, i) => (
          <li key={i}>
            <span className="dot">•</span>
            <div>
              <div style={{ fontWeight: 600 }}>{STATUS_LABELS[h.toStatus] || h.toStatus}</div>
              {h.note && <div className="text-sm">{h.note}</div>}
              <div className="text-sm">{new Date(h.createdAt).toLocaleString("fa-IR")}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
